import { resolveCompanyPeopleStory } from './companyPeopleHeroMotion.js';
import { CompanyPeopleHeroSnap } from './companyPeopleHeroSnap.js';
import { CompanyPeopleHeroBubbles } from './companyPeopleHeroBubbles.js';

/** Native scrolling moves a single track: intro → facts → personal invitation. */
class CompanyPeopleHeroElement extends HTMLElement {
  static observedAttributes = ['data-motion', 'data-editor'];
  private frame = 0;
  private resize?: ResizeObserver;
  private mutation?: MutationObserver;
  private reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  private scrollHost: Window | HTMLElement = window;
  private pinned = false;
  private viewport = window.innerHeight;
  private measuredWidth = 0;
  private enabled = false;
  private panels: HTMLElement[] = [];
  private storyOffset = 0;
  private sceneStart = 0;
  private snap?: CompanyPeopleHeroSnap;
  private bubbles = new CompanyPeopleHeroBubbles(this);

  connectedCallback() {
    for (let parent = this.parentElement; parent; parent = parent.parentElement) {
      if (parent !== document.body && /(auto|scroll)/u.test(getComputedStyle(parent).overflowY)) {
        this.scrollHost = parent;
        break;
      }
    }
    this.snap = new CompanyPeopleHeroSnap(
      this.scrollHost,
      () => {
        const scene = this.querySelector<HTMLElement>('.vp-people-scene');
        if (!this.pinned || !this.enabled || !scene || !this.panels.length) return undefined;
        return {
          start: this.sceneStart,
          viewport: this.viewport,
          steps: this.panels.length,
        };
      },
      moving => {
        this.dataset.snapping = String(moving);
      },
    );
    this.snap.connect();
    window.addEventListener('scroll', this.schedule, { passive: true, capture: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.reduced.addEventListener('change', this.measure);
    this.addEventListener('show-people-contact', this.showContact);
    this.addEventListener('show-people-next', this.showNext);
    this.resize = new ResizeObserver(this.measure);
    this.mutation = new MutationObserver(this.measure);
    this.mutation.observe(this, { childList: true, subtree: true, characterData: true });
    this.measure();
  }

  disconnectedCallback() {
    this.snap?.disconnect();
    this.bubbles.disconnect();
    window.removeEventListener('scroll', this.schedule, { capture: true });
    window.removeEventListener('resize', this.onResize);
    this.reduced.removeEventListener('change', this.measure);
    this.removeEventListener('show-people-contact', this.showContact);
    this.removeEventListener('show-people-next', this.showNext);
    this.resize?.disconnect();
    this.mutation?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.measure();
  }

  private measure = () => {
    this.enabled = this.dataset.motion !== 'off' && this.dataset.editor !== 'true' && !this.reduced.matches;
    const compact = window.innerWidth <= 1024;
    const candidate = this.enabled && window.innerHeight >= (compact ? 560 : 800);
    this.classList.toggle('has-people-pin', candidate);
    // The small viewport stays stable while Safari's browser bars open and close.
    const stage = this.querySelector<HTMLElement>('.vp-people-stage');
    this.viewport = candidate ? stage?.clientHeight || window.innerHeight : window.innerHeight;
    this.measuredWidth = window.innerWidth;
    const contents = [...this.querySelectorAll<HTMLElement>('.vp-people-panel-content')];
    for (const content of contents) this.resize?.observe(content);
    // Oversized editorial copy is allowed to grow in normal document flow.
    this.pinned = candidate && contents.every(content => {
      const panel = content.closest<HTMLElement>('[data-story-panel]');
      if (!panel || !content.offsetHeight) return true;
      const style = getComputedStyle(panel);
      return content.scrollHeight + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) <= this.viewport;
    });
    this.classList.toggle('has-people-pin', this.pinned);
    this.classList.toggle('has-people-motion', this.enabled);
    this.panels = [...this.querySelectorAll<HTMLElement>('[data-story-panel]')].filter(panel => panel.offsetHeight > 0);
    if (this.pinned) {
      this.style.setProperty('--people-viewport-height', `${this.viewport}px`);
      this.style.setProperty('--people-story-height', `${this.panels.length * this.viewport}px`);
    }
    else {
      this.snap?.reset();
      this.style.removeProperty('--people-story-height');
      this.storyOffset = 0;
      this.querySelector<HTMLElement>('.vp-people-center')?.style.removeProperty('--people-story-y');
    }
    this.bubbles.measure(this.pinned && this.enabled, this.storyOffset);
    const scene = this.querySelector<HTMLElement>('.vp-people-scene');
    if (scene) this.sceneStart = this.scrollPosition() + scene.getBoundingClientRect().top - this.hostTop();
    this.schedule();
  };

  private onResize = () => {
    const height = this.querySelector<HTMLElement>('.vp-people-stage')?.clientHeight;
    if (this.pinned && window.innerWidth <= 1024 && window.innerWidth === this.measuredWidth && height === this.viewport)
      return;
    this.measure();
  };

  private hostTop() {
    return this.scrollHost instanceof HTMLElement ? this.scrollHost.getBoundingClientRect().top : 0;
  }

  private scrollPosition() {
    return this.scrollHost instanceof HTMLElement ? this.scrollHost.scrollTop : window.scrollY;
  }

  private schedule = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      const scene = this.querySelector<HTMLElement>('.vp-people-scene');
      const contact = this.querySelector<HTMLElement>('.vp-people-contact');
      const arrow = this.querySelector<HTMLAnchorElement>('.vp-people-scroll');
      if (!scene || !contact) return;
      const scroll = this.pinned ? this.scrollPosition() - this.sceneStart : this.hostTop() - scene.getBoundingClientRect().top;
      const motion = resolveCompanyPeopleStory(scroll, this.viewport, this.panels.length);
      let active = 0;
      let visible = true;
      if (this.pinned) {
        // This transform belongs only to the copy track. Keeping its variable
        // off the root avoids invalidating all portrait styles on every scroll.
        this.storyOffset = -motion.offset;
        this.querySelector<HTMLElement>('.vp-people-center')?.style.setProperty(
          '--people-story-y', `${this.storyOffset}px`,
        );
        this.style.setProperty('--people-open', String(motion.open));
        active = motion.activeStep;
        visible = motion.contactActive;
      } else {
        const clamp = (value: number) => Math.max(0, Math.min(1, value));
        this.style.setProperty('--people-open', String(this.enabled ? clamp(scroll / (window.innerHeight * 0.7)) : 0));
        active = this.panels.reduce(
          (current, panel, index) =>
            panel.getBoundingClientRect().top - this.hostTop() <= window.innerHeight * 0.45 ? index : current,
          0,
        );
        visible = !this.enabled || contact.getBoundingClientRect().top - this.hostTop() < window.innerHeight * 0.8;
      }
      if (this.dataset.storyStep !== String(active)) this.dataset.storyStep = String(active);
      const clamp = (value: number) => Math.max(0, Math.min(1, value));
      const facts = this.querySelector<HTMLElement>('.vp-people-facts-panel');
      const factsOpen =
        !this.enabled || !facts
          ? 1
          : this.pinned
            ? clamp((motion.position - 0.35) / 0.65)
            : clamp(
                (window.innerHeight - facts.getBoundingClientRect().top + this.hostTop()) / (window.innerHeight * 0.6),
              );
      facts?.style.setProperty('--people-facts-open', String(factsOpen));
      this.bubbles.update(this.pinned ? motion.position : 0, factsOpen);
      if (contact.inert !== !visible) contact.inert = !visible;
      if (contact.getAttribute('aria-hidden') !== String(!visible)) contact.setAttribute('aria-hidden', String(!visible));
      this.classList.toggle('is-people-contact-visible', visible);
      if (arrow) arrow.inert = this.pinned && visible;
    });
  };

  private seekPanel(index: number, focusContact = false) {
    this.snap?.reset();
    const panel = this.panels[Math.max(0, Math.min(index, this.panels.length - 1))];
    const scene = this.querySelector<HTMLElement>('.vp-people-scene');
    if (!panel || !scene) return;
    if (this.pinned) {
      this.scrollHost.scrollTo({
        top: this.sceneStart + index * this.viewport,
        behavior: 'instant',
      });
    } else panel.scrollIntoView({ block: 'center', behavior: 'instant' });
    if (focusContact) {
      const contact = this.querySelector<HTMLElement>('.vp-people-contact');
      if (contact) {
        contact.inert = false;
        contact.setAttribute('aria-hidden', 'false');
        contact.focus({ preventScroll: true });
      }
    }
    this.schedule();
  }

  private showContact = () => this.seekPanel(this.panels.length - 1, true);
  private showNext = () => {
    const next = Math.min(Number(this.dataset.storyStep ?? 0) + 1, this.panels.length - 1);
    this.seekPanel(next, next === this.panels.length - 1);
  };
}

if (!customElements.get('vp-company-people-hero'))
  customElements.define('vp-company-people-hero', CompanyPeopleHeroElement);
